import React from 'react';
import { LineItemProps, ServiceOffering } from '@jbhi-fi/apis/src/services/types';
import { JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { AttachedServiceProps } from '@jbhi-fi/components/src/Cart/AttachedService/AttachedService';
import { CartLineItemProps } from '@jbhi-fi/components/src/Cart/CartLineItem/CartLineItem';
import { tryParseJson } from '@jbhi-fi/utils/src/JSON/tryParseJson';
import { updateCart, setCartLoading } from '../../../../utils/cart/cartUtils';
import { CartServiceOfferings } from '../../../../utils/parseServiceOfferings';
import { ServiceIds } from '../../../../utils/constants/services';
import {
  CartItemChangeAlert,
  CartItemChangeAlertProps,
} from '@jbhi-fi/components/src/Cart/CartItemChangeAlert/CartItemChangeAlert';
import { ShopifyStorefrontClient } from '../../../../api/shopify/ShopifyStorefrontClient';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import Alert from '@jbhi-fi/lanyard-ui/components/Alert';
import { format, isValid, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { BundleOfferingProps } from '@jbhi-fi/components/src/Cart/BundleOffering/BundleOffering';
import { transformProductGtmDataForTracking } from '../../../../utils/cart/transformProductGtmDataForTracking';
import {
  transformCartItemForTracking,
  transformCartItemForMessages,
} from '../../../../utils/cart/transformCartItemForTracking';
import { getCartItemChangeAlert } from '../../../../utils/cart/getCartItemChangeAlert';
import { STORE_LOCATIONS } from '../../../../utils/location/locationHelper';
import { palette } from '@jbhi-fi/lanyard-ui/themes';

const storefrontClient = new ShopifyStorefrontClient();

export const getPreOrderText = (dateStr: string | undefined): React.ReactNode => {
  if (!dateStr) return 'Pre-order';
  const preOrderPrefix = 'Pre-order release date';
  const date = parseISO(dateStr);
  if (!isValid(date)) {
    return `${preOrderPrefix} ${dateStr}`;
  }
  const timeZone = window.Jbhifi?.storeLocation === STORE_LOCATIONS.NZ ? 'Pacific/Auckland' : 'Australia/Melbourne';

  const formatted = format(toZonedTime(date, timeZone), 'dd MMM yyyy');
  return `${preOrderPrefix} ${formatted}`;
};

export const getOfferingForCartItem = (lineItem: JbCartItem, cartServiceOfferings?: CartServiceOfferings) => {
  const itemOfferings = cartServiceOfferings?.[lineItem.line_id] as Array<ServiceOffering>;
  const fullItemOfferings = cartServiceOfferings?.[`${lineItem.line_id}|fullOffering`] as LineItemProps;
  if (!itemOfferings?.length) return;

  const desiredTypes = Object.values(ServiceIds);
  const offeredService = desiredTypes
    .map(typeId => itemOfferings.find(o => o.typeId === typeId))
    .find(offering => offering?.shouldRender);

  if (!offeredService || lineItem.isNoLongerAvailable) return;

  return {
    serviceData: offeredService,
    fullOffering: fullItemOfferings,
    trackingMessage: transformCartItemForMessages(lineItem),
  };
};

export abstract class JbCartItemTransformer {
  static toCartLineItemProps(item: JbCartItem, isCartValidated?: boolean): CartLineItemProps {
    const changeAlert = JbCartItemTransformer.toCartItemChangeAlertProps(item);
    const isDigitalDelivery =
      item.properties._availabilityFulfilment === 'Digital' || item.properties.Delivery === 'Digital';
    const isPreOrder = item.properties._availabilityCanPreOrder === 'true';
    const releaseDateText = item.onlineProduct?.Product.ReleaseDateText || item.onlineProduct?.Product.ReleaseDate;

    return {
      title: item.product_title,
      imageUrl: item.image,
      itemUrl: item.url,
      price: item.final_line_price / 100,
      wasPrice: item.original_line_price / 100,
      quantity: item.quantity,
      maxQuantity: Number(item.properties._maxQty),
      marketplaceSeller: item.properties._marketplaceSeller,
      isNoLongerAvailable: item.isNoLongerAvailable,
      isFlipped: item.removed,
      discounts: isCartValidated ? item.discounts.filter(d => Number(d.amount) > 0).map(d => d.message) : [],
      topAlerts: changeAlert ? <CartItemChangeAlert key={item.line_id} {...changeAlert} /> : undefined,
      bottomAlerts: (
        <>
          {isDigitalDelivery ? <Alert type="info" heading="Digital Delivery" inline showIcon /> : undefined}
          {item.isGiftCard && !isDigitalDelivery ? (
            <Alert type="warning" heading="Free Postal Delivery" inline showIcon />
          ) : undefined}
          {isPreOrder ? (
            <Alert
              type="info"
              heading={getPreOrderText(releaseDateText)}
              inline
              showIcon
              customPalette={{ color: palette.accent.purple }}
            />
          ) : undefined}
        </>
      ),
    };
  }

  static toAttachedServiceProps(item: JbCartItem): AttachedServiceProps | undefined {
    const attachedService = item.attachedService;
    if (!attachedService) return;

    const serviceData = tryParseJson<ServiceOffering>(item?.attachedService?.properties._serviceData);
    if (!serviceData) return;
    return {
      name: serviceData.offeringName,
      subtext: serviceData.extraCareCoveragePeriod?.replace(/[\r\n]+/g, ' '),
      price: attachedService.line_price / 100,
      imageUrl: attachedService.image,
      isValid: attachedService.isValidService,
      hideActions: item.isNoLongerAvailable,
    };
  }

  static toCartItemChangeAlertProps = (item: JbCartItem): CartItemChangeAlertProps | undefined => {
    const { variant, priceVariant, currentSellerName } = getCartItemChangeAlert(item);

    if (!variant) return;

    const handleOnDismiss = async () => {
      setCartLoading(true);
      const newProperties = {
        ...item.properties,
        _priceAtc: item.final_line_price,
        _marketplaceTieredIdAtc: item.properties._marketplaceTieredId,
        _marketplaceSellerIdAtc: item.properties._marketplaceSellerId,
      };

      const updatedCart = await storefrontClient.updateCartLine(
        item.line_id,
        item.quantity,
        filterAndMapAttributes(newProperties)
      );
      await updateCart(updatedCart);
    };

    return {
      variant,
      priceVariant,
      currentSellerName,
      handleOnDismiss,
    };
  };

  static toBundleOfferingProps = (item: JbCartItem): BundleOfferingProps | undefined => {
    const suggestion = item.bundleSuggestions?.[0];
    const itemListName = `Bundle buy`;

    if (!suggestion) return;
    const transformedGtmData = transformProductGtmDataForTracking(
      suggestion?.google_tag_manager,
      itemListName,
      suggestion?.variant_id
    );

    return {
      title: suggestion.title,
      imageUrl: suggestion.product_image,
      itemUrl: `/products/${suggestion.handle}`,
      itemListName: itemListName,
      price: suggestion.price,
      tagManagerData: suggestion.google_tag_manager,
      transformedGtmData,
    };
  };

  static toCartItemWishlistProps = (item: JbCartItem) => {
    return {
      productName: item.product_title,
      product_image: item.image,
      trackingData: transformCartItemForTracking(item),
    };
  };
}
